import { useSyncExternalStore } from "react";

/**
 * What the room looks like (PRD v3.3 §2.3a).
 *
 * Three themes, and a fourth answer that is not a theme: `null` means "follow
 * the machine". That distinction is the whole design. The token layer paints
 * light on a bare `:root` and swaps to dark only under
 * `prefers-color-scheme: dark` — so with no attribute at all the system
 * decides, and light wins when the system has no opinion. Writing
 * `data-theme` is therefore not "picking a colour", it is OPTING OUT of the
 * machine's answer, in either direction: a reader on a dark laptop who wants
 * light is exactly as well served as the reverse.
 *
 * `nob` IS THE BRAND SKIN, and it is a different question from the other two.
 * Light and dark answer "how bright"; nob answers "do I want the doorman's own
 * paper-and-ink over the whole app". The reference build keeps that separation
 * in the UI — a labelled switch beside the brightness picker, not a fourth
 * segment inside it (components/you/theme-switcher.tsx) — but underneath it is
 * one setting with four states, because a reader cannot be in two rooms at
 * once and the token layer has exactly one attribute to read.
 *
 * The four PRINTED surfaces — the sign-in, both onboarding runs, the public
 * door and the waiting room — ignore all of this: they are printed for
 * everybody, in every theme (styles/poster.css).
 *
 * The choice is a preference about this browser, not a fact about the account,
 * so it lives in localStorage and never goes near the server.
 */
export const THEMES = ["light", "dark", "nob"] as const;

export type Theme = (typeof THEMES)[number];

/** An explicit theme, or `null` for "whatever the system says". */
export type ThemeChoice = Theme | null;

const KEY = "nob.theme";

/**
 * What the browser paints its OWN furniture with — the mobile URL bar, the
 * task-switcher card. It is the one piece of the page we do not draw, so it
 * has to be told, and it is the reason nob mode reads as nob mode on a phone
 * rather than as a yellow app inside a white browser.
 *
 * The values are the reference build's, character for character. Light is
 * #f2f2f7 rather than the shell's white ground because that is what it uses:
 * the sidebar's grey, which is what sits under the bar on a phone.
 */
const THEME_COLOR: Record<Theme, string> = {
  light: "#f2f2f7",
  dark: "#000000",
  nob: "#fddb2d",
};

/**
 * `null` is not a colour, so the browser furniture has to resolve it the same
 * way the stylesheet does. Everything else is already an answer.
 */
function resolve(choice: ThemeChoice): Theme {
  if (choice !== null) return choice;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

function isTheme(value: unknown): value is Theme {
  return typeof value === "string" && (THEMES as readonly string[]).includes(value);
}

/**
 * Anything we did not write — an older value, a hand-edited key, a browser that
 * refuses storage — reads as "follow the system", because that is the answer
 * that is never wrong. A theme choice is not worth an exception.
 */
export function readThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(KEY);
    return isTheme(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * The attribute goes on <html>, not on a wrapper, so that the page background
 * and the browser's own furniture (scrollbars, form controls, the overscroll
 * gutter) come from the same `color-scheme` the tokens declare.
 */
function paint(choice: ThemeChoice): void {
  const html = document.documentElement;
  if (choice === null) html.removeAttribute("data-theme");
  else html.setAttribute("data-theme", choice);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLOR[resolve(choice)]);
}

let state: ThemeChoice = null;
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Read storage, paint, and remember. Called once from main.tsx before React
 * mounts — the sooner the attribute lands, the less of the system's answer a
 * reader sees before their own.
 */
export function startTheme(): void {
  state = readThemeChoice();
  paint(state);
  watchSystem();
}

/**
 * The stylesheet re-answers `prefers-color-scheme` by itself when a laptop
 * switches at sunset; the `<meta>` cannot, so it is repainted here. Registered
 * once and never removed — it is a document-lifetime listener that reads module
 * state and writes one attribute, so a second one would be a duplicate rather
 * than a leak, and `startTheme` is called on every boot.
 */
let watching = false;

function watchSystem(): void {
  if (watching) return;
  try {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => paint(state));
    watching = true;
  } catch {
    /* A browser without matchMedia keeps the colour it booted with. */
  }
}

export function setThemeChoice(choice: ThemeChoice): void {
  state = choice;
  try {
    if (choice === null) localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    /* A browser that will not store it still gets to look at it. */
  }
  paint(choice);
  for (const listener of listeners) listener();
}

export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => state,
  );
}

/** Test-only reset: the store is module state and outlives React's cleanup. */
export function resetTheme(): void {
  state = null;
  paint(null);
  listeners.clear();
}
